import { useEffect, useState, useMemo, useContext } from 'react';
import { isEqual } from 'lodash';
import {
    amrPipelineCommonActions,
    createAmrPipelineActions, createSearchTransactionActions, imUserConfigActions,
} from '../../../actions/';
import { useSelector } from "react-redux";
import { DealView } from "./deal-view/DealView";
import { ClassView } from "./class-view/ClassView";
import { AppState } from "../../../types/state/AppState";
import { PipelineView } from "../types/PipelineView";
import { PipelineFilter } from "./Filter";
import { SyndicatePopup } from "./SyndicatePopup";
import { EmptyPlaceholder, Preloader } from "../../common";
import IconSVG from "../../../styles/svg-icons";
import { UserConfigType } from "../../../types/user-config/UserConfigType";
import { FilterButton } from "../../filters";
import { CustomizeColumnsDialog } from "../common/customize-columns/CustomizeColumnsDialog";
import { OriginatingTransactionClass } from '../../../types/amr-pipeline/models/OriginatingTransactionClass';
import { AmrClass } from '../../../types/amr-pipeline/models/AmrClass';
import { OriginatingTransaction } from '../../../types/amr-pipeline/models/OriginatingTransaction';
import { AmrTransaction } from '../../../types/amr-pipeline/models/AmrTransaction';
import { OnHoverTooltip } from '../../common';
import { FiltersManagement } from './filters-management/FiltersManagement';
import { ColumnsConfig, FiltersConfig } from '../../../types/user-config/UserConfig';
import PipelineContext from './PipelineContext';
import { amrPipelineSelector } from '../../../selectors/amr-pipeline.selector';
import { pipelineFilters } from '../../../constants/amr-pipeline';
import Summary from './summary/Summary';
import { PipelineType } from '../../../types/amr-pipeline/enums/PipelineType';
import { ImSubscriptionActionBlocker } from '../subscription/ImSubscriptionActionBlocker';
import { SubscriptionFeature } from '../../../types/billing/SubscriptionFeature';
import { RequiredFeature } from '../../access/RequiredFeature';
import { usePortfolios } from '../../../effects/usePortfolios';
import { user } from '../../../user';
import { roles, routes } from '../../../constants';
import { AlertOption } from '../../../types/email-preferences/EmailPreferences';
import { BlockedFeatureContent, SubscribeLink } from "../../../components/access/BlockedFeatureText";
import { alertOptionTooltipText } from '../../../constants/amr-pipeline/alertOptions';
import { useAppDispatch } from '../../../effects/useAppDispatch';
import { TabList, TabItem } from '../../bidding/common/tab-list';
import { matchPath } from 'react-router';
import { KMatrixView } from './k-matrix/KMatrixView';
import { ImAccessType } from '../../../types/amr-pipeline/enums/ImAccessType';
import { ResetTableSortingButton } from '../../bidding/common/table/ResetTableSortingButton';
import classNames from 'classnames';
import { KMatrixViewTypeDropDown } from './k-matrix/KMatrixViewTypeDropDown';

interface PipelineProps {
    viewMode: PipelineView;
    onRedirect: () => void;
    onViewModeChange: (viewMode: PipelineView) => void;
}

export function Pipeline({ viewMode, onRedirect, onViewModeChange }: PipelineProps) {
    const { pipelineType, withBlockedSearchAndFilter } = useContext(PipelineContext);
    const amrPipelineActions = useMemo(() => createAmrPipelineActions(pipelineType), [pipelineType]);
    const searchTransactionsActions = useMemo(() => createSearchTransactionActions(pipelineType), [pipelineType]);

    const dispatch = useAppDispatch();

    const [showColumnsDialog, setShowColumnsDialog] = useState(false);

    const {
        deals: {
            transactions,
            classes,
            isTransactionsSearching,
            isClassesSearching,
            syndicateContacts,
            wasInitialAccessTracked,
            numberOfHiddenTransactions,
            numberOfHiddenClasses,
            kMatrix,
            isKMatrixSearching,
        },
        searchTransactions: { searchTermItems },
        filter: { initialFilter, lastAppliedFilter, filterChanged, filterModified, selectedFilterReferenceName },
    } = useSelector(amrPipelineSelector(pipelineType));

    const columnsConfig = useSelector<AppState, ColumnsConfig[]>(state => state.imUserConfig.columnsConfig);
    const filtersConfig = useSelector<AppState, FiltersConfig>(state => state.imUserConfig.filtersConfig);
    const isLoading = useSelector<AppState, boolean>(state => state.imUserConfig.isLoading);
    const isUpdating = useSelector<AppState, boolean>(state => state.imUserConfig.isUpdating);

    const isFilterApplied = useMemo(() => !isEqual(lastAppliedFilter, initialFilter), [lastAppliedFilter, initialFilter]);
    const isSearching = isTransactionsSearching || isClassesSearching || isKMatrixSearching;
    const isDataLoaded = !!(transactions && classes && kMatrix);
    const isPortfolioDisabled = user.hasRoles(roles.ArrangersClient);
    const isDealView = viewMode === PipelineView.Deal;
    const isClassView = viewMode === PipelineView.Class;
    const isKMatrixView = viewMode === PipelineView.KMatrix;
    const data = isDealView ? transactions : isKMatrixView ? kMatrix : classes;
    const isDataAvailable = isDealView
        ? !!transactions?.length || !numberOfHiddenTransactions
        : isKMatrixView
            ? !!kMatrix?.length || !numberOfHiddenTransactions
            : !!classes?.length || !numberOfHiddenClasses;

    usePortfolios(isPortfolioDisabled);

    useEffect(() => {
        dispatch(imUserConfigActions.getUserConfig(() => {
            dispatch(amrPipelineActions.init());
        }));

        return () => {
            if (!matchPath(window.location.pathname, { path: routes.im, exact: true, strict: false })) {
                dispatch(amrPipelineActions.reset());
                dispatch(searchTransactionsActions.reset());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, amrPipelineActions, searchTransactionsActions]);

    useEffect(() => {
        !isUpdating && setShowColumnsDialog(false);
    }, [isUpdating]);

    useEffect(() => {
        if (!wasInitialAccessTracked && isDataLoaded && pipelineType === PipelineType.Deals) {
            dispatch(amrPipelineCommonActions.logImUserActivity(ImAccessType.issuanceMonitor));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDataLoaded])

    const renderView = () => {
        const data = isDealView ? transactions : classes;

        if (viewMode !== PipelineView.KMatrix && (searchTermItems.length || (isFilterApplied && filterChanged)) && !data?.length) {
            return (
                <EmptyPlaceholder
                    onResetClick={handleResetFilterAndSearch}
                />
            );
        }

        switch (viewMode) {
            case PipelineView.Deal:
                return (
                    data && <DealView
                        transactions={data as (OriginatingTransaction | AmrTransaction)[]}
                        columnsConfig={tabColumnsConfig}
                        onRedirect={onRedirect}
                    />
                );
            case PipelineView.Class:
                return (
                    data && <ClassView
                        classes={data as (OriginatingTransactionClass | AmrClass)[]}
                        columnsConfig={tabColumnsConfig}
                        onRedirect={onRedirect}
                    />
                );
            case PipelineView.KMatrix:
                return <KMatrixView onRedirect={onRedirect} />;
            default:
                return null;
        }
    };

    const tabColumnsConfig = useMemo(() => {
        const viewConfig = columnsConfig.find(({ type }) => {
            return type === (isDealView
                ? UserConfigType.imColumnsDealTab
                : UserConfigType.imColumnsClassTab
            )
        });

        if (!viewConfig) {
            return [];
        }

        return (viewConfig as ColumnsConfig).value
            .filter(column => !!column.displayed)
            .sort((a, b) => a.order - b.order);
    }, [columnsConfig, isDealView]);

    if (!isDataLoaded || !initialFilter || isLoading) {
        return <Preloader inProgress={true} />;
    }

    const handleToggleFilterDefault = (referenceName: string, isDefault: boolean) => {
        dispatch(imUserConfigActions.setUserFilterParams({
            referenceName: referenceName,
            default: isDefault,
        }, UserConfigType.imFilter));
    };

    const handleSaveFilterAlerts = (referenceName: string, alertOption: AlertOption, isDefault = false) => {
        dispatch(imUserConfigActions.setUserFilterParams({
            referenceName,
            default: isDefault,
            alertOption,
        }, UserConfigType.imFilter));
    };

    const handleDeleteFilter = (referenceName: string) =>
        dispatch(imUserConfigActions.deleteUserFilter(referenceName, UserConfigType.imFilter));

    const renderFiltersManagement = () => (
        <ImSubscriptionActionBlocker>
            {blocked => (
                <FiltersManagement
                    filterType={pipelineType}
                    filters={filtersConfig[UserConfigType.imFilter].value}
                    onSaveFilterAlert={handleSaveFilterAlerts}
                    onMakeDefault={handleToggleFilterDefault}
                    onResetDefault={handleToggleFilterDefault}
                    onDeleteFilter={handleDeleteFilter}
                    selectedFilterReferenceName={selectedFilterReferenceName}
                    filterChanged={filterChanged}
                    filterModified={filterModified}
                    disabled={blocked}
                    onChange={() => dispatch(amrPipelineActions.apply())}
                    alertOptionInfo={{
                        [AlertOption.Instant]: alertOptionTooltipText[AlertOption.Instant],
                        [AlertOption.Daily]: alertOptionTooltipText[AlertOption.Daily],
                    }}
                />
            )}
        </ImSubscriptionActionBlocker>
    );

    const renderColumnsCustomization = () => (
        <ImSubscriptionActionBlocker>
            {blocked => (
                <>
                    <OnHoverTooltip overlay={!blocked && "Customize Columns"}>
                        <FilterButton
                            className="btn-link"
                            onClick={() => setShowColumnsDialog(true)}
                            disabled={isSearching || blocked}
                        >
                            <IconSVG name="grid-swap" width={16} height={16} />
                            Columns
                        </FilterButton>
                    </OnHoverTooltip>
                    {showColumnsDialog && (
                        <CustomizeColumnsDialog defaultTab={viewMode as PipelineView.Deal | PipelineView.Class} onClose={() => setShowColumnsDialog(false)} />
                    )}
                </>
            )}
        </ImSubscriptionActionBlocker>
    );

    const handleApplyFilter = () => dispatch(amrPipelineActions.apply());
    const handleResetFilter = () => dispatch(amrPipelineActions.filterReset());

    const handleResetFilterAndSearch = () => {
        dispatch(searchTransactionsActions.reset());
        handleResetFilter();
    };

    const handleFilterVisibilityChange = (filterName: string) => dispatch(amrPipelineActions.handleVisibilityFilter(filterName));

    const handleSortingReset = () => {
        switch (viewMode) {
            case PipelineView.Deal: dispatch(amrPipelineActions.dealSortingChanged()); break
            case PipelineView.Class: dispatch(amrPipelineActions.classSortingChanged()); break
            default: dispatch(amrPipelineActions.kMatrixSortingChanged()); break
        }

        dispatch(amrPipelineActions.applyFilterAndSearch(viewMode));
    }

    return (
        <>
            <div className="sub-header sub-header-pipeline">
                <div className="sub-header-row type02 sub-header-filters row-align-filters">
                    <PipelineFilter
                        onVisibilityChange={handleFilterVisibilityChange}
                        hiddenFilters={pipelineFilters.additionalFilters}
                        isSearching={isTransactionsSearching || isClassesSearching}
                        onApply={handleApplyFilter}
                        onReset={handleResetFilter}
                    />
                    <div className="flex-row save-manage-filters flex-none">
                        {renderFiltersManagement()}
                    </div>
                </div>
            </div>
            <div className="sub-header sub-header-pipeline sub-header-tabs-columns">
                <div className="sub-header-row flex-row">
                    <TabList>
                        <TabItem
                            title="Deal View"
                            active={viewMode === PipelineView.Deal}
                            onClick={() => onViewModeChange(PipelineView.Deal)}
                            disabled={withBlockedSearchAndFilter}
                        />
                        <TabItem
                            title="Class View"
                            active={viewMode === PipelineView.Class}
                            onClick={() => onViewModeChange(PipelineView.Class)}
                            disabled={withBlockedSearchAndFilter}
                        />
                        {pipelineType !== PipelineType.IOIs && (
                            <TabItem
                                title="K-Matrix"
                                active={viewMode === PipelineView.KMatrix}
                                onClick={() => onViewModeChange(PipelineView.KMatrix)}
                                disabled={withBlockedSearchAndFilter}
                            />
                        )}
                    </TabList>
                    <div className={classNames("flex-item-right flex-row", { "controls": isDealView || isClassView })}>
                        <ResetTableSortingButton
                            tableKey={viewMode.toString()}
                            onReset={handleSortingReset}
                        />
                        {isKMatrixView && (
                            <>
                                <span className="vertical-divider"></span>
                                <KMatrixViewTypeDropDown />
                            </>
                        )}
                        {(isDealView || isClassView) && renderColumnsCustomization()}
                    </div>
                </div>
            </div>
            <div className="container-flex pipeline-table position-relative">
                <Preloader inProgress={isSearching && !data?.length}>
                    <RequiredFeature
                        feature={SubscriptionFeature.IssuanceMonitorFullAccess}
                        renderBlockedContent={() => (
                            <BlockedFeatureContent
                                inline
                                className="im-stats-placeholder"
                                text={
                                    <>
                                        <SubscribeLink /> to see Deal and Class stats.
                                    </>
                                }
                            />
                        )}
                    >
                        <Summary />
                    </RequiredFeature>
                    <RequiredFeature
                        feature={SubscriptionFeature.IssuanceMonitorFullAccess}
                        overrideRequiredFeatures={isDataAvailable}
                        renderBlockedContent={() => (
                            <BlockedFeatureContent
                                inline
                                className="empty-placeholder"
                                text={
                                    <>
                                        <SubscribeLink /> to see the {!isClassView ? 'deals' : 'classes'}.
                                    </>
                                }
                            />
                        )}
                    >
                        {renderView()}
                    </RequiredFeature>
                </Preloader>
            </div>
            {syndicateContacts && (
                <SyndicatePopup
                    syndicateContacts={syndicateContacts}
                    onClose={() => dispatch(amrPipelineActions.resetSyndicateContacts())}
                />
            )}
        </>
    );
}
